import React, { useState, useEffect, useRef } from 'react';
import api from '../../api/client';
import { useAuth } from '../../contexts/AuthContext';
import { fmt, fmtDt, ago, fileSize, errMsg } from '../../utils/helpers';
import { FILE_TYPES } from '../../utils/statuses';
import { Spinner, Select, ConfirmModal, Modal, Field } from './UI';
import { Send, Lock, Download, Trash2, Upload, Activity, X, Eye, ZoomIn, ZoomOut } from 'lucide-react';
import toast from 'react-hot-toast';

// ── Comments ───────────────────────────────────────────────────
export function CommentsPanel({ entityType, entityId, comments = [], onRefresh }) {
  const { user, isProducer } = useAuth();
  const [body, setBody] = useState('');
  const [isInternal, setIsInternal] = useState(false);
  const [visibleToManager, setVisibleToManager] = useState(false);
  const [sending, setSending] = useState(false);
  const [deleteConfirm, setDeleteConfirm] = useState(null);

  const send = async () => {
    if (!body.trim()) return;
    setSending(true);
    try {
      await api.post('/' + entityType + 's/' + entityId + '/comments', {
        body,
        is_internal: isInternal,
        visible_to_manager: visibleToManager,
      });
      setBody('');
      setIsInternal(false);
      setVisibleToManager(false);
      onRefresh();
    } catch (err) { toast.error(errMsg(err)); }
    finally { setSending(false); }
  };

  const deleteComment = async (cid) => {
    try {
      await api.delete('/' + entityType + 's/comments/' + cid);
      toast.success('Комментарий удалён');
      onRefresh();
    } catch (err) { toast.error(errMsg(err)); }
  };

  // Можно удалить свой комментарий в течение 30 минут
  const canDelete = (c) => {
    if (!user) return false;
    if (c.author_id !== user.id) return false;
    const mins = (Date.now() - new Date(c.created_at)) / 60000;
    return mins <= 30;
  };

  return (
    <div className="space-y-4">
      <div className="space-y-3">
        {!comments.length && <p className="text-sm text-gray-600">Нет комментариев</p>}
        {comments.map(c => (
          <div key={c.id} className={`rounded-lg p-3 border group ${
            c.is_internal ? 'bg-yellow-900/10 border-yellow-800/40' : 'bg-gray-800/60 border-gray-800'
          }`}>
            <div className="flex items-center justify-between mb-1.5">
              <div className="flex items-center gap-2">
                <span className="text-xs font-medium text-gray-300">{c.author_name || '—'}</span>
                {c.is_internal && (
                  <span className="flex items-center gap-1 text-xs text-yellow-500">
                    <Lock size={10} /> внутренний
                  </span>
                )}
                {!c.is_internal && c.visible_to_manager && (
                  <span className="flex items-center gap-1 text-xs text-blue-400">
                    <Eye size={10} /> виден менеджеру
                  </span>
                )}
              </div>
              <div className="flex items-center gap-2">
                <span className="text-xs text-gray-600" title={fmtDt(c.created_at)}>{ago(c.created_at)}</span>
                {canDelete(c) && (
                  <button className="btn-ghost p-0.5 hover:text-red-400 opacity-0 group-hover:opacity-100 transition-opacity"
                    title="Удалить комментарий (доступно 30 мин)"
                    onClick={() => setDeleteConfirm(c)}>
                    <X size={12} />
                  </button>
                )}
              </div>
            </div>
            <p className="text-sm text-gray-200 whitespace-pre-wrap">{c.body}</p>
          </div>
        ))}
      </div>
      <div className="border-t border-gray-800 pt-4">
        <textarea className="input resize-none" rows={3} placeholder="Написать комментарий..."
          value={body} onChange={e => setBody(e.target.value)}
          onKeyDown={e => { if (e.ctrlKey && e.key === 'Enter') send(); }} />
        <div className="flex items-center justify-between gap-4 mt-2">
          <div className="flex flex-wrap items-center gap-4">
            {isProducer && (
              <label className="flex items-center gap-2 text-xs text-gray-400 cursor-pointer select-none">
                <input
                  type="checkbox"
                  className="accent-yellow-500"
                  checked={isInternal}
                  onChange={e => {
                    const checked = e.target.checked;
                    setIsInternal(checked);
                    if (checked) setVisibleToManager(false);
                  }}
                />
                <Lock size={11} /> Внутренний (скрыт от дилера)
              </label>
            )}
            {user && user.role !== 'dealer_user' && (
              <label className={`flex items-center gap-2 text-xs cursor-pointer select-none ${isInternal ? 'text-gray-600' : 'text-gray-400'}`}>
                <input
                  type="checkbox"
                  className="accent-blue-500"
                  checked={visibleToManager}
                  disabled={isInternal}
                  onChange={e => setVisibleToManager(e.target.checked)}
                />
                <Eye size={11} /> Виден менеджеру
              </label>
            )}
          </div>
          <button className="btn-primary gap-2" onClick={send} disabled={sending || !body.trim()}>
            {sending ? <Spinner size={14} /> : <Send size={14} />} Отправить
          </button>
        </div>
        <p className="text-xs text-gray-700 mt-1">Ctrl+Enter для отправки</p>
      </div>
    </div>
  );
}

// ── Files ──────────────────────────────────────────────────────
// Модалка для загрузки одного файла с выбором типа и комментарием
function UploadFileModal({ entityType, entityId, onClose, onDone }) {
  const [file, setFile] = useState(null);
  const [fileType, setFileType] = useState('other');
  const [comment, setComment] = useState('');
  const [uploading, setUploading] = useState(false);
  const ref = useRef();

  const fileTypeOpts = Object.entries(FILE_TYPES).map(([v, l]) => ({ value: v, label: l }));

  const handleFile = (f) => setFile(f);

  const upload = async () => {
    if (!file) { toast.error('Выберите файл'); return; }
    setUploading(true);
    const fd = new FormData();
    fd.append('file', file);
    fd.append('entity_type', entityType);
    fd.append('entity_id', entityId);
    fd.append('file_type', fileType);
    fd.append('comment', comment);
    try {
      await api.post('/files', fd);
      toast.success(file.name + ' загружен');
      onDone();
      onClose();
    } catch (err) { toast.error(errMsg(err)); }
    finally { setUploading(false); }
  };

  return (
    <Modal title="Загрузить файл" onClose={onClose} size="sm">
      <div className="space-y-3">
        {/* Зона выбора файла */}
        <div
          className={`border-2 border-dashed rounded-lg p-5 text-center cursor-pointer transition-colors
            ${file ? 'border-blue-600 bg-blue-900/10' : 'border-gray-700 hover:border-gray-600'}`}
          onClick={() => ref.current?.click()}
        >
          <Upload size={20} className="mx-auto mb-2 text-gray-500" />
          {file ? (
            <p className="text-sm text-blue-300 font-medium">{file.name}</p>
          ) : (
            <p className="text-sm text-gray-500">Нажмите для выбора файла</p>
          )}
          <p className="text-xs text-gray-600 mt-1">Макс. 15MB</p>
          <input ref={ref} type="file" className="hidden"
            onChange={e => e.target.files[0] && handleFile(e.target.files[0])} />
        </div>

        <Field label="Категория файла">
          <Select value={fileType} onChange={setFileType} options={fileTypeOpts} />
        </Field>

        <Field label="Комментарий к файлу">
          <input className="input" value={comment} onChange={e => setComment(e.target.value)}
            placeholder="Необязательно" />
        </Field>

        <div className="flex justify-end gap-2 pt-2">
          <button className="btn-secondary" onClick={onClose}>Отмена</button>
          <button className="btn-primary" onClick={upload} disabled={uploading || !file}>
            {uploading ? <Spinner size={14} /> : <><Upload size={14} /> Загрузить</>}
          </button>
        </div>
      </div>
    </Modal>
  );
}


// ── Просмотр файла ─────────────────────────────────────────────
function FileViewerModal({ file, onClose }) {
  const ext = (file.original_name || '').split('.').pop().toLowerCase();
  const isImage = ['jpg','jpeg','png','gif','webp'].includes(ext);
  const isPdf = ext === 'pdf';
  const [blobUrl, setBlobUrl] = useState(null);
  const [loading, setLoading] = useState(true);
  const [imgScale, setImgScale] = useState(1);

  useEffect(() => {
    const token = localStorage.getItem('access_token');
    fetch('/api/files/' + file.id, { headers: { Authorization: 'Bearer ' + token } })
      .then(r => r.blob())
      .then(blob => {
        setBlobUrl(URL.createObjectURL(blob));
        setLoading(false);
      })
      .catch(() => setLoading(false));
    return () => { if (blobUrl) URL.revokeObjectURL(blobUrl); };
  }, [file.id]);

  const download = () => {
    if (!blobUrl) return;
    const a = document.createElement('a');
    a.href = blobUrl; a.download = file.original_name; a.click();
  };

  if (!isImage && !isPdf) return null;

  return (
    <div className="fixed inset-0 z-50 flex flex-col bg-black/95"
      onClick={e => e.target === e.currentTarget && onClose()}>
      <div className="flex items-center justify-between px-4 py-3 bg-gray-900/80 border-b border-gray-800 shrink-0">
        <div>
          <div className="text-sm font-medium text-gray-200">{file.original_name}</div>
          <div className="text-xs text-gray-500">{FILE_TYPES[file.file_type] || file.file_type}</div>
        </div>
        <div className="flex items-center gap-2">
          {isImage && blobUrl && (
            <>
              <button className="btn-ghost p-1.5" onClick={() => setImgScale(s => Math.max(0.25, s - 0.25))}><ZoomOut size={16} /></button>
              <span className="text-xs text-gray-500 w-10 text-center mono">{Math.round(imgScale * 100)}%</span>
              <button className="btn-ghost p-1.5" onClick={() => setImgScale(s => Math.min(4, s + 0.25))}><ZoomIn size={16} /></button>
              <button className="btn-ghost p-1.5 text-xs text-gray-500" onClick={() => setImgScale(1)}>1:1</button>
            </>
          )}
          <button className="btn-secondary text-xs flex items-center gap-1 py-1.5 px-3" onClick={download} disabled={!blobUrl}>
            <Download size={13} /> Скачать
          </button>
          <button className="btn-ghost p-1.5 hover:text-red-400" onClick={onClose}><X size={18} /></button>
        </div>
      </div>
      <div className="flex-1 overflow-auto flex items-center justify-center p-4">
        {loading && <div className="text-gray-500">Загрузка...</div>}
        {!loading && !blobUrl && <div className="text-red-400">Ошибка загрузки файла</div>}
        {blobUrl && isImage && (
          <img src={blobUrl} alt={file.original_name}
            style={{ transform: `scale(${imgScale})`, transformOrigin: 'center', transition: 'transform 0.15s', maxWidth: '100%', maxHeight: '100%' }}
            className="object-contain rounded shadow-2xl" />
        )}
        {blobUrl && isPdf && (
          <iframe src={blobUrl} title={file.original_name}
            className="w-full h-full rounded" style={{ minHeight: '80vh' }} />
        )}
      </div>
    </div>
  );
}

export function FilesPanel({ entityType, entityId, files = [], onRefresh }) {
  const { isProducer, hasPerm } = useAuth();
  const [showUpload, setShowUpload] = useState(false);
  const [deleteConfirm, setDeleteConfirm] = useState(null);
  const [viewFile, setViewFile] = useState(null);

  const deleteFile = async (id) => {
    try {
      await api.delete('/files/' + id);
      toast.success('Файл удалён');
      onRefresh();
    } catch (err) { toast.error(errMsg(err)); }
  };

  const canPreview = (name) => {
    const ext = (name || '').split('.').pop().toLowerCase();
    return ['jpg','jpeg','png','gif','webp','pdf'].includes(ext);
  };

  const extIcon = (name) => {
    const ext = (name || '').split('.').pop().toLowerCase();
    if (['jpg','jpeg','png','gif','webp'].includes(ext)) return '🖼';
    if (ext === 'pdf') return '📄';
    if (['xls','xlsx'].includes(ext)) return '📊';
    if (['doc','docx'].includes(ext)) return '📝';
    if (['zip','rar'].includes(ext)) return '🗜';
    return '📎';
  };

  return (
    <div className="space-y-4">
      {(isProducer || hasPerm('files', 'upload')) && (
        <button className="btn-primary" onClick={() => setShowUpload(true)}>
          <Upload size={14} /> Загрузить файл
        </button>
      )}

      <div className="space-y-1.5">
        {!files.length && <p className="text-sm text-gray-600">Нет вложений</p>}
        {files.map(f => (
          <div key={f.id} className="flex items-center gap-3 p-2.5 bg-gray-800/60 border border-gray-800 rounded-lg group">
            <span className="text-lg">{extIcon(f.original_name)}</span>
            <div className="flex-1 min-w-0">
              <div className={`text-sm text-gray-200 truncate ${canPreview(f.original_name) ? 'cursor-pointer hover:text-blue-300 transition-colors' : ''}`}
                onClick={() => canPreview(f.original_name) && setViewFile(f)}>
                {f.original_name}
              </div>
              <div className="text-xs text-gray-500">
                {FILE_TYPES[f.file_type] || f.file_type}
                {f.comment && <span className="text-gray-600 ml-2">· {f.comment}</span>}
              </div>
              <div className="text-xs text-gray-600">
                {fileSize(f.size_bytes)} · {fmt(f.uploaded_at)} · {f.uploaded_by_name}
              </div>
            </div>
            <div className="flex items-center gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
              {canPreview(f.original_name) && (
                <button className="btn-ghost p-1.5 text-blue-400 hover:text-blue-300" title="Просмотр"
                  onClick={() => setViewFile(f)}>
                  <Eye size={14} />
                </button>
              )}
              <a href={'/api/files/' + f.id} download={f.original_name}
                className="btn-ghost p-1.5" title="Скачать">
                <Download size={14} />
              </a>
              {(isProducer || hasPerm('files', 'delete')) && (
                <button className="btn-ghost p-1.5 hover:text-red-400" title="Удалить"
                  onClick={() => setDeleteConfirm(f)}>
                  <Trash2 size={14} />
                </button>
              )}
            </div>
          </div>
        ))}
      </div>

      {showUpload && (
        <UploadFileModal
          entityType={entityType} entityId={entityId}
          onClose={() => setShowUpload(false)}
          onDone={onRefresh}
        />
      )}

      {deleteConfirm && (
        <ConfirmModal
          message={'Удалить файл "' + deleteConfirm.original_name + '"?'}
          onConfirm={() => deleteFile(deleteConfirm.id)}
          onClose={() => setDeleteConfirm(null)}
        />
      )}

      {viewFile && <FileViewerModal file={viewFile} onClose={() => setViewFile(null)} />}
    </div>
  );
}

// ── Activity log ───────────────────────────────────────────────
const ACTION_LABELS = {
  create: 'Создано',
  update: 'Изменено',
  update_content: 'Изменено содержание',
  status_change: 'Смена статуса',
  update_progress: 'Обновлён прогресс',
  add_payment: 'Добавлен платёж',
  comment: 'Добавлен комментарий',
  upload_file: 'Загружен файл',
  delete_file: 'Удалён файл',
  delete: 'Удалено',
  add_item: 'Добавлена позиция',
  add_kit: 'Добавлен комплект',
  shipment: 'Создана отгрузка',
  shipment_received: 'Подтверждено получение отгрузки',
  login: 'Вход в систему',
  logout: 'Выход из системы',
  create_user: 'Создан пользователь',
  update_user: 'Изменён пользователь',
  delete_user: 'Удалён пользователь',
  change_password: 'Изменён пароль',
  update_permissions: 'Изменены права пользователя',
  create_company: 'Создана компания',
  update_company: 'Изменена компания',
  delete_company: 'Удалена компания',
  update_dealer: 'Изменён дилер',
  delete_dealer: 'Удалён дилер',
};

const ENTITY_LABELS = {
  order: 'Заказ',
  repair: 'Ремонт',
  supply: 'Поставка',
  user: 'Пользователь',
  dealer: 'Дилер',
  company: 'Компания',
};

function activityTitle(e) {
  return e.payload?.order_number || e.payload?.repair_number || e.payload?.supply_number || e.payload?.target_name || e.payload?.title || e.payload?.item_name || e.payload?.description || e.payload?.name || null;
}

export function ActivityLog({ entries = [] }) {
  return (
    <div className="space-y-1">
      {!entries.length && <p className="text-sm text-gray-600">Нет записей</p>}
      {entries.map(e => {
        const title = activityTitle(e);
        return (
          <div key={e.id} className="flex items-start gap-3 py-2 border-b border-gray-800/60 text-sm">
            <Activity size={13} className="text-gray-600 mt-0.5 shrink-0" />
            <div className="flex-1 min-w-0">
              <div className="flex flex-wrap items-center gap-x-2 gap-y-1">
                {e.entity_type && <span className="text-xs text-gray-500 uppercase tracking-wide">{ENTITY_LABELS[e.entity_type] || e.entity_type}</span>}
                <span className="text-gray-300">{ACTION_LABELS[e.action] || e.action}</span>
                {title && <span className="text-gray-500">«{title}»</span>}
                {e.payload?.status && <span className="text-gray-500">→ {e.payload.status}</span>}
                {e.payload?.amount && <span className="text-gray-500">{e.payload.amount} ₽</span>}
              </div>
              <div className="text-xs text-gray-600 mt-0.5">{e.user_name || e.actor_username || '—'}</div>
            </div>
            <span className="text-xs text-gray-700 shrink-0" title={fmtDt(e.created_at)}>{ago(e.created_at)}</span>
          </div>
        );
      })}
    </div>
  );
}
